/* Self-contained cart-storage reader for the CartSideSheet container. The app
   (src/utils/UtilityFunctions.ts) ships its own `getSavedCart` for pre-existing
   consumers; this file replicates the picker so DDS can read the same slot
   without importing across the src/ boundary the charter forbids.
   ⚠ Keep the keys and the `isEmptyCart` predicate in sync with
   `getSavedCart` in src/utils/UtilityFunctions.ts — if the app writes a new cartType or key, both readers have
   to change.

   WAT-12195: shop saves every streaming and genre cart under `build` (cartType
   "streaming" or "genre" for UCCP, "streaming" or "build" for MCCP, which also
   sets planType "mccp"). Satellite still saves under `satellite`. No other key
   is read. */

export const CART_STORAGE_KEYS = ["build", "satellite"] as const;

const isEmptyObj = (obj: unknown): boolean => !obj || typeof obj !== "object" || Object.keys(obj as object).length === 0;

/* Mirrors the app's `isEmptyCart`. Opening shop writes an empty `build` cart
   straight away, so an empty cart never wins. A genre cart carries the
   container offer in `package` before any pack is picked, so it is judged by
   its base packs. */

const isEmptyCart = (cart: any): boolean => {
  if (cart?.cartType === "satellite") {
    return (
      isEmptyObj(cart?.package) &&
      !(cart?.addOns?.length > 0) &&
      !((cart?.equipment?.devices?.length ?? 0) > 0)
    );
  }
  if (["genre", "build"].includes(cart?.cartType)) {
    return !(cart?.genreBasePackages?.length > 0);
  }
  return !cart?.cartType || isEmptyObj(cart?.package);
};

const readCart = (key: string): any => {
  try {
    return JSON.parse(window?.localStorage?.getItem?.(key) || "null");
  } catch (err) {
    console.error(`[getLatestCartWithStorageKey] could not read saved cart "${key}"`, err);
    return null;
  }
};

/* Epoch ms of the cart's last package change; missing or unparseable → 0. */
const cartTime = (cart: any): number => new Date(cart?.packageDetail?.lastUpdated ?? 0)?.getTime() || 0;

export interface LatestCartWithKey {
  cart: any;
  /** The `CART_STORAGE_KEYS` entry the cart came out of — `build` or
   *  `satellite`. Callers that need to delete or rewrite the persisted cart
   *  MUST use this, not `cart.cartType`: build carts carry `cartType:
   *  "streaming" | "genre" | "build"`, so the two disagree. */
  storageKey: (typeof CART_STORAGE_KEYS)[number];
}

/** The shopper's cart and the key it was persisted under. A shopper can hold a
 *  build cart and a satellite cart at once, so when both have content the one
 *  changed last wins. An undated cart counts as oldest, and a tie goes to `build`. */
export function getLatestCartWithStorageKey(): LatestCartWithKey | null {
  // Touching `window.localStorage` itself throws when storage is blocked, so readCart's try owns that check.
  if (typeof window !== "object") {
    return null;
  }

  const [build, satellite] = CART_STORAGE_KEYS.map(storageKey => {
    const cart = readCart(storageKey);
    return isEmptyCart(cart) ? null : {
      cart,
      storageKey
    };
  });
  if (!build || !satellite) {
    return build ?? satellite ?? null;
  }
  return cartTime(satellite?.cart) > cartTime(build?.cart) ? satellite : build;
}

/* `storage` only fires in OTHER tabs, so a write made on this page announces
   itself with this event instead. Anything that reads the saved cart while the
   page is open — the nav's cart badge — listens for both. */
export const CART_CHANGE_EVENT = "dds:cart-change";

export function notifyCartChange(): void {
  if (typeof window === "object") {
    window.dispatchEvent(new Event(CART_CHANGE_EVENT));
  }
}

/** Remove-all: clears every cart slot, as the shipping drawer does, then
    announces it. Clearing only the newest slot would let the other cart take
    its place in the badge and the sheet. Storage blocked → nothing to clear. */
export function clearSavedCarts(): void {
  try {
    CART_STORAGE_KEYS.forEach(key => window.localStorage.removeItem(key));
  } catch {
    return;
  }
  notifyCartChange();
}
